import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import FinanceV2Layout from "./FinanceV2Layout";
import { NoteState } from "../../context/notes/NoteState";
import FinanceV2Services from "../../UserServices/FinanceV2Services";
import "./FinanceV2.css";

const TODAY = () => new Date().toISOString().split("T")[0];

function accountLabel(a) {
  const parent = a.parent?.name || a.parent_name;
  const base = `${a.code} — ${a.name}`;
  return parent ? `${base}  ·  ${parent}` : base;
}

export default function FinanceV2Transactions() {
  const { state } = useContext(NoteState);
  const { branch_id } = state;
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "expense";
  const [tab, setTab] = useState(
    ["expense", "transfer", "charity", "liability", "history"].includes(initialTab)
      ? initialTab
      : "expense"
  );
  const [accounts, setAccounts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [setupReady, setSetupReady] = useState(null); // null=loading, true/false
  const [loadError, setLoadError] = useState("");

  const [expense, setExpense] = useState({
    entry_date: TODAY(),
    amount: "",
    expense_account_id: "",
    payment_account_id: "",
    vendor_id: "",
    description: "",
    is_liability: false,
  });
  const [transfer, setTransfer] = useState({
    entry_date: TODAY(),
    amount: "",
    from_account_id: "",
    to_account_id: "",
    description: "",
  });
  const [charity, setCharity] = useState({
    entry_date: TODAY(),
    debit: "",
    credit: "",
    payment_account_id: "",
    expense_account_id: "",
    income_account_id: "",
    description: "",
  });
  const [liability, setLiability] = useState({
    entry_date: TODAY(),
    amount: "",
    liability_account_id: "",
    payment_account_id: "",
    description: "",
  });

  useEffect(() => {
    const t = searchParams.get("tab") || "expense";
    if (["expense", "transfer", "charity", "liability", "history"].includes(t)) {
      setTab(t);
    }
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;
    setLoadError("");
    setSetupReady(null);

    Promise.all([
      FinanceV2Services.getAccountsList(branch_id),
      FinanceV2Services.getVendors(branch_id),
      FinanceV2Services.getSetupStatus(branch_id),
    ])
      .then(([accRes, vendRes, setupRes]) => {
        if (cancelled) return;
        setAccounts(accRes.data?.data || []);
        setVendors(vendRes.data?.data || []);
        const completed =
          setupRes.data?.data?.setup_completed === true ||
          setupRes.data?.data?.setup_completed === 1 ||
          setupRes.data?.setup_completed === true;
        setSetupReady(!!completed);
      })
      .catch((err) => {
        if (cancelled) return;
        const msg =
          err.response?.data?.message ||
          "Could not load accounts / setup. Check login and branch.";
        setLoadError(msg);
        setSetupReady(false);
        toast.error(msg);
      });

    return () => {
      cancelled = true;
    };
  }, [branch_id]);

  const switchTab = (t) => {
    setTab(t);
    setSearchParams(t === "expense" ? {} : { tab: t });
  };

  const formsLocked = setupReady !== true;

  const assetAccounts = useMemo(
    () => accounts.filter((a) => a.account_type?.code === "asset"),
    [accounts]
  );
  const expenseAccounts = useMemo(
    () => accounts.filter((a) => a.account_type?.code === "expense"),
    [accounts]
  );
  const incomeAccounts = useMemo(
    () => accounts.filter((a) => a.account_type?.code === "income"),
    [accounts]
  );
  const liabilityAccounts = useMemo(
    () => accounts.filter((a) => a.account_type?.code === "liability"),
    [accounts]
  );

  const submitExpense = async (e) => {
    e.preventDefault();
    if (formsLocked) {
      toast.error("Finish Finance V2 setup before posting.");
      return;
    }
    if (!expense.expense_account_id) {
      toast.error("Select expense head");
      return;
    }
    if (!expense.is_liability && !expense.payment_account_id) {
      toast.error("Select paid-from account (or check On account)");
      return;
    }
    setSaving(true);
    try {
      await FinanceV2Services.recordExpense({
        entry_date: expense.entry_date,
        amount: parseFloat(expense.amount),
        branch_id,
        expense_account_id: expense.expense_account_id,
        payment_account_id: expense.is_liability
          ? undefined
          : expense.payment_account_id,
        vendor_id: expense.vendor_id || undefined,
        description: expense.description,
        is_liability: expense.is_liability,
      });
      toast.success("Expense posted to Finance V2 books");
      setExpense({
        entry_date: TODAY(),
        amount: "",
        expense_account_id: expense.expense_account_id,
        payment_account_id: expense.payment_account_id,
        vendor_id: "",
        description: "",
        is_liability: false,
      });
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const submitTransfer = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await FinanceV2Services.recordTransfer({
        ...transfer,
        amount: parseFloat(transfer.amount),
        branch_id,
      });
      toast.success("Transfer posted");
      setTransfer({
        entry_date: TODAY(),
        amount: "",
        from_account_id: transfer.from_account_id,
        to_account_id: transfer.to_account_id,
        description: "",
      });
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const submitCharity = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await FinanceV2Services.recordCharity({
        ...charity,
        debit: parseFloat(charity.debit) || 0,
        credit: parseFloat(charity.credit) || 0,
        branch_id,
        payment_account_id: charity.payment_account_id || undefined,
        expense_account_id: charity.expense_account_id || undefined,
        income_account_id: charity.income_account_id || undefined,
      });
      toast.success("Charity entry posted");
      setCharity({
        entry_date: TODAY(),
        debit: "",
        credit: "",
        payment_account_id: charity.payment_account_id,
        expense_account_id: charity.expense_account_id,
        income_account_id: charity.income_account_id,
        description: "",
      });
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const submitLiability = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await FinanceV2Services.recordLiabilityPayment({
        ...liability,
        amount: parseFloat(liability.amount),
        branch_id,
      });
      toast.success("Liability payment posted");
      setLiability({
        entry_date: TODAY(),
        amount: "",
        liability_account_id: liability.liability_account_id,
        payment_account_id: liability.payment_account_id,
        description: "",
      });
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const backfillExpenses = async () => {
    try {
      const res = await FinanceV2Services.backfillLegacyExpenses({
        branch_id,
        process_all: true,
        limit: 1000,
      });
      toast.success(
        `History import: ${res.data?.data?.synced} synced, ${res.data?.data?.failed} failed`
      );
    } catch (e) {
      toast.error("Import failed");
    }
  };

  const tabs = [
    { id: "expense", label: "Expense" },
    { id: "transfer", label: "Transfer / bank / petty" },
    { id: "charity", label: "Charity" },
    { id: "liability", label: "Pay liability" },
    { id: "history", label: "History import only" },
  ];

  return (
    <FinanceV2Layout>
      <div className="container-fluid px-0 py-2">
        <div className="d-flex justify-content-between align-items-start mb-2 flex-wrap gap-2">
          <div>
            <h4 className="mb-1">Enter transactions</h4>
            <p className="small text-muted mb-0">
              Optional Finance V2 entry screen (expense, transfer, charity, liability). You can also
              keep using the classic Finance menus (Add Expense, Bank, Charity, etc.) as usual.
              Reports:{" "}
              <Link to="/finance-v2/reports">Financial reports</Link>,{" "}
              <Link to="/finance-v2/general-ledger">General ledger</Link>,{" "}
              <Link to="/finance-v2/journals">Journals</Link>.
            </p>
          </div>
          <Link to="/finance-v2/dashboard" className="btn btn-sm btn-outline-secondary">
            Dashboard
          </Link>
        </div>

        <div className="alert alert-success py-2 small mb-3">
          Pick the exact expense head (e.g. Media → Facebook Ads). Paid from = Cash on Hand,
          Bank, or Petty Cash. Books update immediately.
        </div>

        {loadError ? (
          <div className="alert alert-danger py-2 small mb-3">{loadError}</div>
        ) : null}

        {setupReady === false && !loadError ? (
          <div className="alert alert-warning py-2 small mb-3">
            Finance V2 setup is not finished for this branch. Finish{" "}
            <Link to="/finance-v2/setup">Setup wizard</Link> (step 7 — Launch) before posting.
            Entry forms are locked until setup is complete.
          </div>
        ) : null}

        {setupReady === true ? (
          <div className="alert alert-primary py-2 small mb-3">
            Finance V2 books are ready. Classic Finance entry screens also remain available.
          </div>
        ) : null}

        <ul className="nav nav-tabs mb-3">
          {tabs.map((t) => (
            <li className="nav-item" key={t.id}>
              <button
                type="button"
                className={`nav-link ${tab === t.id ? "active" : ""}`}
                onClick={() => switchTab(t.id)}
              >
                {t.label}
              </button>
            </li>
          ))}
        </ul>

        {tab === "expense" && (
          <div className={`fv2-card ${formsLocked ? "opacity-50" : ""}`}>
            <form onSubmit={submitExpense}>
              <div className="row g-2">
                <div className="col-md-2">
                  <label className="form-label small">Date</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={expense.entry_date}
                    onChange={(e) => setExpense({ ...expense, entry_date: e.target.value })}
                    required
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label small">Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    className="form-control form-control-sm"
                    value={expense.amount}
                    onChange={(e) => setExpense({ ...expense, amount: e.target.value })}
                    required
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label small">Expense account (head)</label>
                  <select
                    className="form-select form-select-sm"
                    value={expense.expense_account_id}
                    onChange={(e) =>
                      setExpense({ ...expense, expense_account_id: e.target.value })
                    }
                    required
                  >
                    <option value="">Select expense head…</option>
                    {expenseAccounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {accountLabel(a)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label small">Paid from (cash / bank / petty)</label>
                  <select
                    className="form-select form-select-sm"
                    value={expense.payment_account_id}
                    onChange={(e) =>
                      setExpense({ ...expense, payment_account_id: e.target.value })
                    }
                    required={!expense.is_liability}
                    disabled={expense.is_liability}
                  >
                    <option value="">Select account…</option>
                    {assetAccounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {accountLabel(a)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label small">Vendor (optional)</label>
                  <select
                    className="form-select form-select-sm"
                    value={expense.vendor_id}
                    onChange={(e) => setExpense({ ...expense, vendor_id: e.target.value })}
                  >
                    <option value="">—</option>
                    {vendors.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4 d-flex align-items-end">
                  <div className="form-check mb-1">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="expAp"
                      checked={expense.is_liability}
                      onChange={(e) =>
                        setExpense({ ...expense, is_liability: e.target.checked })
                      }
                    />
                    <label className="form-check-label small" htmlFor="expAp">
                      On account (credit A/P instead of cash)
                    </label>
                  </div>
                </div>
                <div className="col-12">
                  <input
                    className="form-control form-control-sm"
                    placeholder="Description / bill note"
                    value={expense.description}
                    onChange={(e) =>
                      setExpense({ ...expense, description: e.target.value })
                    }
                  />
                </div>
              </div>
              <button
                type="submit"
                className="btn btn-sm btn-success mt-2"
                disabled={saving || formsLocked}
              >
                Post expense to books
              </button>
            </form>
          </div>
        )}

        {tab === "transfer" && (
          <div className="fv2-card">
            <p className="small text-muted mb-2">
              Move money between Cash, Bank, Petty Cash, or Clearing — replaces old bank / petty /
              office cash &quot;add&quot; screens.
            </p>
            <form onSubmit={submitTransfer}>
              <div className="row g-2">
                <div className="col-md-2">
                  <label className="form-label small">Date</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={transfer.entry_date}
                    onChange={(e) => setTransfer({ ...transfer, entry_date: e.target.value })}
                    required
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label small">Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    className="form-control form-control-sm"
                    value={transfer.amount}
                    onChange={(e) => setTransfer({ ...transfer, amount: e.target.value })}
                    required
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label small">From</label>
                  <select
                    className="form-select form-select-sm"
                    value={transfer.from_account_id}
                    onChange={(e) =>
                      setTransfer({ ...transfer, from_account_id: e.target.value })
                    }
                    required
                  >
                    <option value="">From account…</option>
                    {assetAccounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {accountLabel(a)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label small">To</label>
                  <select
                    className="form-select form-select-sm"
                    value={transfer.to_account_id}
                    onChange={(e) =>
                      setTransfer({ ...transfer, to_account_id: e.target.value })
                    }
                    required
                  >
                    <option value="">To account…</option>
                    {assetAccounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {accountLabel(a)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-12">
                  <input
                    className="form-control form-control-sm"
                    placeholder="Description"
                    value={transfer.description}
                    onChange={(e) =>
                      setTransfer({ ...transfer, description: e.target.value })
                    }
                  />
                </div>
              </div>
              <button type="submit" className="btn btn-sm btn-success mt-2" disabled={saving || formsLocked}>
                Post transfer
              </button>
            </form>
          </div>
        )}

        {tab === "charity" && (
          <div className="fv2-card">
            <p className="small text-muted mb-2">
              Paid out = Debit charity expense, Credit cash/bank. Received = Debit cash, Credit
              charity income. Pick the exact heads (e.g. 5300.1).
            </p>
            <form onSubmit={submitCharity}>
              <div className="row g-2">
                <div className="col-md-2">
                  <label className="form-label small">Date</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={charity.entry_date}
                    onChange={(e) => setCharity({ ...charity, entry_date: e.target.value })}
                    required
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label small">Paid out</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="form-control form-control-sm"
                    value={charity.debit}
                    onChange={(e) => setCharity({ ...charity, debit: e.target.value })}
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label small">Received</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="form-control form-control-sm"
                    value={charity.credit}
                    onChange={(e) => setCharity({ ...charity, credit: e.target.value })}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label small">Cash / bank used</label>
                  <select
                    className="form-select form-select-sm"
                    value={charity.payment_account_id}
                    onChange={(e) =>
                      setCharity({ ...charity, payment_account_id: e.target.value })
                    }
                  >
                    <option value="">Default cash</option>
                    {assetAccounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {accountLabel(a)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label small">Charity expense head (paid out)</label>
                  <select
                    className="form-select form-select-sm"
                    value={charity.expense_account_id}
                    onChange={(e) =>
                      setCharity({ ...charity, expense_account_id: e.target.value })
                    }
                  >
                    <option value="">Default 5300</option>
                    {expenseAccounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {accountLabel(a)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label small">Charity income head (received)</label>
                  <select
                    className="form-select form-select-sm"
                    value={charity.income_account_id}
                    onChange={(e) =>
                      setCharity({ ...charity, income_account_id: e.target.value })
                    }
                  >
                    <option value="">Default 4070</option>
                    {incomeAccounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {accountLabel(a)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-12">
                  <input
                    className="form-control form-control-sm"
                    placeholder="Description"
                    value={charity.description}
                    onChange={(e) =>
                      setCharity({ ...charity, description: e.target.value })
                    }
                  />
                </div>
              </div>
              <button type="submit" className="btn btn-sm btn-success mt-2" disabled={saving || formsLocked}>
                Post charity
              </button>
            </form>
          </div>
        )}

        {tab === "liability" && (
          <div className="fv2-card">
            <p className="small text-muted mb-2">
              Pay down a liability (loan, A/P, etc.): reduces the liability and cash/bank.
            </p>
            <form onSubmit={submitLiability}>
              <div className="row g-2">
                <div className="col-md-2">
                  <label className="form-label small">Date</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={liability.entry_date}
                    onChange={(e) =>
                      setLiability({ ...liability, entry_date: e.target.value })
                    }
                    required
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label small">Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    className="form-control form-control-sm"
                    value={liability.amount}
                    onChange={(e) => setLiability({ ...liability, amount: e.target.value })}
                    required
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label small">Liability account</label>
                  <select
                    className="form-select form-select-sm"
                    value={liability.liability_account_id}
                    onChange={(e) =>
                      setLiability({ ...liability, liability_account_id: e.target.value })
                    }
                    required
                  >
                    <option value="">Select liability…</option>
                    {liabilityAccounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {accountLabel(a)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label small">Paid from</label>
                  <select
                    className="form-select form-select-sm"
                    value={liability.payment_account_id}
                    onChange={(e) =>
                      setLiability({ ...liability, payment_account_id: e.target.value })
                    }
                    required
                  >
                    <option value="">Select cash/bank…</option>
                    {assetAccounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {accountLabel(a)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-12">
                  <input
                    className="form-control form-control-sm"
                    placeholder="Description"
                    value={liability.description}
                    onChange={(e) =>
                      setLiability({ ...liability, description: e.target.value })
                    }
                  />
                </div>
              </div>
              <button type="submit" className="btn btn-sm btn-success mt-2" disabled={saving || formsLocked}>
                Post liability payment
              </button>
            </form>
          </div>
        )}

        {tab === "history" && (
          <div className="fv2-card">
            <p className="small text-muted">
              One-time / catch-up only: pull <strong>old</strong> expense rows into V2 journals.
              Do not keep entering new expenses in the legacy module — enter them on the Expense
              tab above. Prefer{" "}
              <Link to="/finance-v2/import">Import &amp; sync</Link> for controlled historical
              imports.
            </p>
            <button type="button" className="btn btn-sm btn-outline-primary" onClick={backfillExpenses} disabled={formsLocked}>
              Import historical legacy expenses
            </button>
          </div>
        )}
      </div>
    </FinanceV2Layout>
  );
}
